import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { insureds, type Insured } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { AVISO_BLOQUEIO } from './rotulos';

/**
 * "Bloquear" do segurado, com o motivo obrigatorio, como o SetBlocked do OnPoint.
 *
 * O aviso diz o que o bloqueio faz hoje: grava e mostra. O efeito na cotacao e na
 * emissao nao tem fonte (SEG-006).
 */
export function BlockInsuredDialog({
  insured,
  onClose,
  onDone,
}: {
  insured: Insured | null;
  onClose: () => void;
  onDone: (bloqueado: Insured) => void;
}) {
  const toast = useToast();
  const [motivo, setMotivo] = useState('');

  const bloquear = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      insureds.block(id, reason),
    onSuccess: (segurado) => {
      toast.success(`Segurado ${segurado.legal_name} bloqueado.`);
      setMotivo('');
      onDone(segurado);
    },
  });

  return (
    <Modal
      open={insured !== null}
      onClose={() => {
        setMotivo('');
        bloquear.reset();
        onClose();
      }}
      title="Deseja realmente bloquear o segurado?"
      description={insured?.legal_name}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={bloquear.isPending}
            disabled={motivo.trim() === ''}
            onClick={() =>
              insured ? bloquear.mutate({ id: insured.id, reason: motivo }) : undefined
            }
          >
            Confirmar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {bloquear.error ? (
          <Alert tone="danger" title="Nao foi possivel bloquear">
            {bloquear.error.message}
          </Alert>
        ) : null}
        <p className="text-xs text-ink-600">{AVISO_BLOQUEIO}</p>
        <Textarea
          label="Motivo do bloqueio"
          required
          value={motivo}
          onChange={(event) => setMotivo(event.target.value)}
        />
      </div>
    </Modal>
  );
}
